import { z } from 'zod';
import { DeliveryState, EvidenceBasis } from './enums.js';
import { IsoDateTime, Reason, UserRef, Uuid, Version, boundedList } from './common.js';

export const RepositoryView = z.object({
  id: Uuid,
  /** GITHUB (GitHub App) or GITEA (the Vammo Apps internal git, ADR-0037). */
  provider: z.enum(['GITHUB', 'GITEA']),
  externalId: z.string(),
  fullName: z.string(),
  defaultBranch: z.string().nullable(),
  url: z.string().nullable(),
  archived: z.boolean(),
  projects: z.array(z.object({ id: Uuid, key: z.string(), name: z.string() })),
  lastSyncedAt: IsoDateTime.nullable(),
  basis: EvidenceBasis,
});
export type RepositoryView = z.infer<typeof RepositoryView>;

export const PullRequestView = z.object({
  id: Uuid,
  repository: z.object({ id: Uuid, fullName: z.string() }),
  number: z.number().int(),
  title: z.string(),
  state: z.enum(['OPEN', 'CLOSED', 'MERGED']),
  draft: z.boolean(),
  authorLogin: z.string().nullable(),
  author: UserRef.nullable(),
  url: z.string().nullable(),
  headRef: z.string().nullable(),
  headSha: z.string().nullable(),
  mergeCommitSha: z.string().nullable(),
  mergeMethod: z.enum(['MERGE', 'SQUASH', 'REBASE', 'UNKNOWN']).nullable(),
  mergedAt: IsoDateTime.nullable(),
  additions: z.number().int().nullable(),
  deletions: z.number().int().nullable(),
  changedFiles: z.number().int().nullable(),
  checks: z.object({ passed: z.number().int(), failed: z.number().int(), pending: z.number().int() }),
  linkedWorkItems: z.array(z.object({ id: Uuid, key: z.string(), title: z.string(), linkBasis: z.string() })),
  basis: EvidenceBasis,
  updatedAt: IsoDateTime,
});
export type PullRequestView = z.infer<typeof PullRequestView>;

export const EnvironmentClassification = z.enum(['PRODUCTION', 'STAGING', 'DEVELOPMENT', 'PREVIEW', 'OTHER']);
export type EnvironmentClassification = z.infer<typeof EnvironmentClassification>;

export const ServiceView = z.object({
  id: Uuid,
  name: z.string(),
  externalId: z.string(),
  repositories: z.array(
    z.object({
      repositoryId: Uuid,
      fullName: z.string(),
      buildPath: z.string().nullable(),
      /** What the provider builds the service with (for example Dockerfile.backend). */
      buildFile: z.string().nullable(),
      /** Repository paths whose change reaches the service (ADR-0029); `*` is the whole repository, empty is not computed yet. */
      footprint: z.array(z.string()),
      footprintSource: z.enum(['WORKSPACE', 'DOCKERFILE', 'REPOSITORY', 'MANUAL']).nullable(),
    }),
  ),
  environments: z.array(
    z.object({ id: Uuid, name: z.string(), externalId: z.string(), classification: EnvironmentClassification, targetSlot: z.string().nullable() }),
  ),
  activeProductionArtifacts: z.array(z.object({ environmentId: Uuid, artifact: z.string().nullable(), commitSha: z.string().nullable(), since: IsoDateTime.nullable() })),
  basis: EvidenceBasis,
});
export type ServiceView = z.infer<typeof ServiceView>;

export const NormalizedDeploymentStatus = z.enum(['PENDING', 'IN_PROGRESS', 'SUCCEEDED', 'FAILED', 'CANCELED', 'ROLLED_BACK', 'UNKNOWN']);
export type NormalizedDeploymentStatus = z.infer<typeof NormalizedDeploymentStatus>;

export const DeploymentView = z.object({
  id: Uuid,
  externalId: z.string(),
  service: z.object({ id: Uuid, name: z.string() }),
  environment: z.object({ id: Uuid, name: z.string(), classification: EnvironmentClassification, targetSlot: z.string().nullable() }),
  status: NormalizedDeploymentStatus,
  sourceStatus: z.string(),
  sourceOrdering: z.string().nullable(),
  artifactDigest: z.string().nullable(),
  commitSha: z.string().nullable(),
  startedAt: IsoDateTime.nullable(),
  completedAt: IsoDateTime.nullable(),
  observedAt: IsoDateTime,
  basis: EvidenceBasis,
});
export type DeploymentView = z.infer<typeof DeploymentView>;

export const ChangeRefKind = z.enum(['PULL_REQUEST', 'COMMIT', 'ARTIFACT']);
export const ChangeRef = z.object({
  kind: ChangeRefKind,
  /** PR UUID, full commit SHA or immutable artifact digest. */
  ref: z.string().min(1).max(200),
  repositoryId: Uuid.optional(),
});
export type ChangeRef = z.infer<typeof ChangeRef>;

export const RequirementCoverage = z.enum(['COVERED', 'UNCOVERED', 'UNKNOWN']);
export type RequirementCoverage = z.infer<typeof RequirementCoverage>;

export const DeliveryRequirementView = z.object({
  id: Uuid,
  service: z.object({ id: Uuid, name: z.string() }),
  environment: z.object({ id: Uuid, name: z.string(), classification: EnvironmentClassification }),
  targetSlot: z.string().nullable(),
  changes: z.array(ChangeRef.extend({ resolved: z.boolean(), display: z.string() })),
  coverage: RequirementCoverage,
  coverageReason: z.string().nullable(),
  coveringDeployment: z.object({ id: Uuid, externalId: z.string(), completedAt: IsoDateTime.nullable() }).nullable(),
});
export type DeliveryRequirementView = z.infer<typeof DeliveryRequirementView>;

export const DeliveryScopeView = z.object({
  workItemId: Uuid,
  scopeRevision: z.number().int(),
  status: z.enum(['NONE', 'AWAITING_SCOPE', 'UNCONFIRMED', 'CONFIRMED']),
  confirmationAuthority: z.enum(['EXPLICIT', 'APPROVED_MAPPING']).nullable(),
  confirmedBy: UserRef.nullable(),
  confirmedAt: IsoDateTime.nullable(),
  mappingRuleVersion: z.string().nullable(),
  rationale: z.string().nullable(),
  requirements: z.array(DeliveryRequirementView),
  exclusions: z.array(z.object({ serviceId: Uuid, serviceName: z.string(), reason: z.string() })),
  deliveryState: DeliveryState,
  suggestions: z.array(z.object({ serviceId: Uuid, serviceName: z.string(), reason: z.string() })),
});
export type DeliveryScopeView = z.infer<typeof DeliveryScopeView>;

export const SetDeliveryScope = z
  .object({
    expectedVersion: Version,
    requirements: boundedList(
      z
        .object({
          serviceId: Uuid,
          environmentId: Uuid,
          targetSlot: z.string().max(80).optional(),
          changes: boundedList(ChangeRef, 50),
        })
        .strict(),
      50,
    ),
    exclusions: boundedList(z.object({ serviceId: Uuid, reason: Reason }).strict(), 50).optional(),
    rationale: Reason,
    confirm: z.boolean(),
  })
  .strict();
export type SetDeliveryScope = z.infer<typeof SetDeliveryScope>;

export const LinkPullRequest = z
  .object({ workItemId: Uuid, pullRequestId: Uuid })
  .strict();
export type LinkPullRequest = z.infer<typeof LinkPullRequest>;

/** A commit associated with a work item. `url` opens it on the provider. */
export const CommitView = z.object({
  id: Uuid,
  repository: z.object({ id: Uuid, fullName: z.string() }),
  sha: z.string(),
  shortSha: z.string(),
  messageSummary: z.string(),
  authorLogin: z.string().nullable(),
  committedAt: IsoDateTime.nullable(),
  url: z.string().nullable(),
  /** VAMMO_APPS_CHANGE: the commit of a Vammo Apps change (ADR-0037). */
  linkBasis: z.enum(['EXPLICIT_KEY', 'MANUAL', 'VAMMO_APPS_CHANGE']),
  linkedBy: UserRef.nullable(),
  linkedAt: IsoDateTime,
});
export type CommitView = z.infer<typeof CommitView>;

/**
 * Link a commit or pull request to a work item: paste its GitHub URL
 * (https://github.com/org/repo/commit/<sha> or .../pull/<n>), or name the repository with a SHA
 * (7 to 40 hex characters) or a pull request number.
 */
export const LinkWorkCode = z
  .object({
    url: z.url({ protocol: /^https$/ }).max(500).optional(),
    repositoryFullName: z.string().regex(/^[\w.-]+\/[\w.-]+$/).max(200).optional(),
    sha: z.string().regex(/^[0-9a-fA-F]{7,40}$/).optional(),
    pullNumber: z.number().int().positive().optional(),
  })
  .strict()
  .refine((v) => Boolean(v.url) !== Boolean(v.repositoryFullName && (v.sha || v.pullNumber)), { message: 'Provide a GitHub URL, or a repository with a SHA or pull request number' });
export type LinkWorkCode = z.infer<typeof LinkWorkCode>;

export const LinkWorkCodeResult = z.object({ kind: z.enum(['COMMIT', 'PULL_REQUEST']), id: Uuid });

const RepositoryFullName = z.string().regex(/^[\w.-]+\/[\w.-]+$/).max(200);

/**
 * Everything an agent knows about the code behind a work item, linked in one call: the branch it
 * works on (its pull requests and the commits not yet on the default branch), explicit commit SHAs
 * and PR numbers, and GitHub URLs. Each reference is linked or skipped on its own.
 */
export const LinkCodeFields = z
  .object({
    repositoryFullName: RepositoryFullName.optional().describe('owner/repo, as in the git remote; needed for branch, shas and pullNumbers'),
    branch: z.string().min(1).max(250).optional().describe('Branch being worked on; links its pull requests and the commits ahead of the default branch'),
    shas: boundedList(z.string().regex(/^[0-9a-fA-F]{7,40}$/), 50).optional().describe('Pushed commit SHAs, for example commits made directly on the default branch'),
    pullNumbers: boundedList(z.number().int().positive(), 20).optional(),
    urls: boundedList(z.url({ protocol: /^https$/ }).max(500), 20).optional().describe('GitHub commit or pull request URLs'),
  })
  .strict();
const linkCodeRefinement = (v: z.infer<typeof LinkCodeFields>) =>
  Boolean(v.branch || v.shas?.length || v.pullNumbers?.length || v.urls?.length) && (Boolean(v.repositoryFullName) || !(v.branch || v.shas?.length || v.pullNumbers?.length));
const linkCodeMessage = { message: 'Provide a branch, SHAs, pull request numbers or GitHub URLs; branch, SHAs and numbers also need repositoryFullName' };
export const LinkCode = LinkCodeFields.refine(linkCodeRefinement, linkCodeMessage);
export type LinkCode = z.infer<typeof LinkCode>;
export const LinkCodeRefinement = { check: linkCodeRefinement, message: linkCodeMessage };

export const LinkCodeResult = z.object({
  linked: z.array(z.object({ kind: z.enum(['COMMIT', 'PULL_REQUEST']), id: Uuid, ref: z.string(), url: z.string().nullable() })),
  skipped: z.array(z.object({ ref: z.string(), code: z.string(), message: z.string() })),
});
export type LinkCodeResult = z.infer<typeof LinkCodeResult>;

export const SetProjectRepositories = z
  .object({ expectedVersion: Version, repositoryIds: boundedList(Uuid, 200) })
  .strict();

export const SetServiceMapping = z
  .object({
    repositories: boundedList(
      z
        .object({
          repositoryId: Uuid,
          buildPath: z.string().max(300).optional(),
          /** Paths whose change reaches the service, instead of the inferred footprint (ADR-0029). */
          paths: boundedList(z.string().min(1).max(300), 100).optional(),
        })
        .strict(),
      50,
    ),
    environments: boundedList(
      z.object({ environmentId: Uuid, classification: EnvironmentClassification, targetSlot: z.string().max(80).optional() }).strict(),
      50,
    ),
  })
  .strict();
export type SetServiceMapping = z.infer<typeof SetServiceMapping>;
